import { useEffect, useMemo, useState } from 'react';
import { getSites } from '../../services/siteService';
import { DuplicateWarning } from '../../components/forms/DuplicateWarning';
import type { Site } from '../../types/project';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { createSite } from '../../services/siteService';
import { COUNTRIES, provincesForCountry } from '../../constants/geography';

interface NewSiteDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const NewSiteDialog = ({ open, onClose, onCreated }: NewSiteDialogProps) => {
  const [siteName, setSiteName] = useState('');
  const [siteCity, setSiteCity] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [province, setProvince] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState<Site[]>([]);

  useEffect(() => {
    if (open) getSites().then(setExisting).catch(() => setExisting([]));
  }, [open]);

  const provinceOptions = useMemo(() => provincesForCountry(country), [country]);

  const resetForm = () => {
    setSiteName('');
    setSiteCity('');
    setCountry(null);
    setProvince(null);
    setError('');
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleSubmit = async () => {
    if (!siteName.trim()) { setError('Site Name is required'); return; }
    // City is now optional (TASK-003). At least one of City / Province / Country should be present.
    if (!siteCity.trim() && !province && !country) {
      setError('Please enter at least a City, Province, or Country.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await createSite({
        SiteName: siteName.trim(),
        SiteCity: siteCity.trim() || null,
        Province: province,
        Country: country,
      });
      resetForm();
      onCreated();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to create site.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New Site</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField size="small" label="Site Name" required value={siteName}
          onChange={(e) => setSiteName(e.target.value)} autoFocus />
        <DuplicateWarning
          value={siteName}
          candidates={existing}
          nameSelector={(s) => s.SiteName}
          secondarySelector={(s) => s.SiteCity || ''}
          label="Existing sites with similar names:"
        />
        <TextField size="small" label="City" value={siteCity}
          onChange={(e) => setSiteCity(e.target.value)}
          helperText="Optional — some remote sites are not near any city" />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Autocomplete size="small" freeSolo options={COUNTRIES}
            value={country}
            onChange={(_e, v) => { setCountry(typeof v === 'string' ? v : v); setProvince(null); }}
            onInputChange={(_e, v) => setCountry(v || null)}
            renderInput={(params) => <TextField {...params} label="Country" />} />
          <Autocomplete size="small" freeSolo options={provinceOptions}
            value={province}
            onChange={(_e, v) => setProvince(typeof v === 'string' ? v : v)}
            onInputChange={(_e, v) => setProvince(v || null)}
            renderInput={(params) => <TextField {...params} label="Province / State" />} />
        </Box>
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
          Create Site
        </Button>
      </DialogActions>
    </Dialog>
  );
};
